import * as Sentry from '@sentry/gatsby';
import {
  isSameDay,
  startOfDay,
  parseISO,
  isBefore,
  isAfter,
  addMonths,
} from 'date-fns';
import { convertedTime } from './convertedTime';
import { ServiceLocation, removeTimePartFromDate } from '../../helpers';
import {
  AvailableService,
  AvailableServicesInZipResponse,
  AvailableStore,
  AvailableStoresResponse,
  AvailableTimeslotsResponse,
  LocationType,
  PriceWithSavings,
  Savings,
  DisplayPrice,
  Slots,
  TimeSlots,
  CustomerPrice,
  PriceOfferType,
  ReplacementService,
} from './model';
import {
  getFormattedPrice,
  isIkeaMembershipSavings,
} from '../../helpers/currencyHelper';
import { isBusinessOperation } from '../../helpers/urlHelper';
import { CustomerType } from '../../reducers/user/userTypes';

const getTimeSlotDates = (timeSlot: TimeSlots): [string, string] => {
  return [timeSlot.dateFrom, timeSlot.dateTo];
};

const sortTimeSlots = (timeslots: TimeSlots[]): TimeSlots[] => {
  const timeslotsWithSortingKeys: [string, string, TimeSlots][] = timeslots.map(
    (timeslot) => {
      return [...getTimeSlotDates(timeslot), timeslot];
    },
  );
  timeslotsWithSortingKeys.sort((a, b) => {
    const index = a.findIndex((value, i) => i < a.length - 1 && value !== b[i]);
    if (index !== -1) {
      return a[index] < b[index] ? -1 : 1;
    }
    return 0;
  });
  timeslotsWithSortingKeys.forEach((timeslot, index) => {
    timeslots[index] = timeslot[2];
  });
  return timeslots;
};

const getLocation = (locationType: LocationType): ServiceLocation => {
  switch (locationType) {
    case 'STORE':
      return 'inStore';
    case 'STORE_REMOTE':
      return 'storeRemote';
    case 'HOME':
      return 'home';
    case 'STORE_HOME':
      return 'storeHome';
    default:
      return 'remote';
  }
};

const getExclSavingsPrice = (priceObject: PriceWithSavings): number => {
  return priceObject.price.priceInclTax || priceObject.price.priceExclTax || 0;
};

const getInclSavingsPrice = (priceObject: PriceWithSavings): number => {
  return (
    priceObject.price.priceInclTaxWithSavings ||
    priceObject.price.priceExclTaxWithSavings ||
    0
  );
};

type SavingsDateSpan = {
  validFromDateUTC?: string;
  validToDateUTC?: string;
};

const getValidDateSpan = (
  priceOfferType: PriceOfferType,
  savings?: Savings[],
): SavingsDateSpan => {
  if (!savings || savings.length === 0) {
    return {};
  }
  const dateSpan = savings.reduce((acc, saving): SavingsDateSpan => {
    const { validFromDateUTC, validToDateUTC } = acc;
    if (!validFromDateUTC) {
      acc.validFromDateUTC = saving.validFromDateUTC;
    } else if (isBefore(validFromDateUTC, saving.validFromDateUTC)) {
      acc.validFromDateUTC = saving.validFromDateUTC;
    }
    if (!validToDateUTC) {
      acc.validToDateUTC = saving.validToDateUTC;
    } else if (isAfter(validToDateUTC, saving.validToDateUTC)) {
      acc.validToDateUTC = saving.validToDateUTC;
    }
    return acc;
  }, {} as SavingsDateSpan);

  if (
    isIkeaMembershipSavings(priceOfferType) &&
    dateSpan.validToDateUTC &&
    isAfter(dateSpan.validToDateUTC, addMonths(Date.now(), 6))
  ) {
    return {};
  }

  return dateSpan;
};

const getPriceOfService = (
  regularPrice: number,
  currencyCode: string,
  priceWithSavings?: PriceWithSavings,
  priceOfferType?: PriceOfferType,
): DisplayPrice => {
  const inclSavings = priceWithSavings
    ? getInclSavingsPrice(priceWithSavings)
    : regularPrice;
  const { validFromDateUTC, validToDateUTC } = priceOfferType
    ? getValidDateSpan(priceOfferType, priceWithSavings?.savings)
    : {};
  return {
    currencyCode,
    exclSavings: regularPrice,
    exclSavingsFormatted: getFormattedPrice(regularPrice, currencyCode),
    inclSavings,
    inclSavingsFormatted: getFormattedPrice(inclSavings, currencyCode),
    totalSavings: priceWithSavings?.price.totalDiscount || 0,
    priceOfferType,
    validFromDateUTC,
    validToDateUTC,
  };
};

const getCustomerPrice = (
  customerType: CustomerType,
  allowedMemberDiscounts: boolean,
  isBusiness: boolean,
  regularPrice: number,
  currencyCode: string,
  familyPriceObject?: PriceWithSavings,
  businessPriceObject?: PriceWithSavings,
  guestPriceObject?: PriceWithSavings,
): CustomerPrice => {
  let price = regularPrice;
  let totalSavings: number | undefined;
  let priceOfferType: PriceOfferType | undefined;
  const troSavings = guestPriceObject?.price.totalDiscount || 0;
  if (
    !isBusiness &&
    customerType === 'Individual' &&
    familyPriceObject &&
    familyPriceObject.price.totalDiscount &&
    familyPriceObject.price.totalDiscount > troSavings
  ) {
    price = getInclSavingsPrice(familyPriceObject);
    totalSavings = familyPriceObject.price.totalDiscount;
    priceOfferType = 'family';
  } else if (
    customerType === 'Business' &&
    allowedMemberDiscounts &&
    businessPriceObject &&
    businessPriceObject.price.totalDiscount &&
    businessPriceObject.price.totalDiscount > troSavings
  ) {
    price = getInclSavingsPrice(businessPriceObject);
    totalSavings = businessPriceObject.price.totalDiscount;
    priceOfferType = 'ibn';
  } else if (guestPriceObject && guestPriceObject?.price.totalDiscount) {
    price = getInclSavingsPrice(guestPriceObject);
    totalSavings = guestPriceObject.price.totalDiscount;
    priceOfferType = 'tro';
  }
  return {
    price,
    priceFormatted: getFormattedPrice(price, currencyCode),
    totalSavings,
    priceOfferType,
  };
};

const getServicePrice = (
  prices: PriceWithSavings[],
  operation: string,
  customerType: CustomerType,
  allowedMemberDiscounts: boolean,
): { displayPrice: DisplayPrice; customerPrice: CustomerPrice } => {
  const guestPriceObject = prices.find(
    (priceObj) => priceObj.price.priceType === 'Private',
  );
  const familyPriceObject = prices.find(
    (priceObj) => priceObj.price.priceType === 'Family',
  );
  const businessPriceObject = prices.find(
    (priceObj) => priceObj.price.priceType === 'Business',
  );
  const availablePriceObject =
    guestPriceObject || familyPriceObject || businessPriceObject;
  if (!availablePriceObject) {
    Sentry.captureException('Missing price object for service');
    throw new Error('Missing price object for service');
  }

  const isFamilyPriceAllowed =
    !isBusinessOperation(operation) && customerType !== 'Business';
  const isBusinessPriceAllowed =
    isBusinessOperation(operation) || customerType !== 'Individual';
  const regularPrice = getExclSavingsPrice(availablePriceObject);
  const currencyCode = availablePriceObject.price.currencyCode;
  const familySavings = familyPriceObject?.price.totalDiscount || 0;
  const businessSavings = businessPriceObject?.price.totalDiscount || 0;
  const guestSavings = guestPriceObject?.price.totalDiscount || 0;

  let priceObjectWithSavings: PriceWithSavings | undefined;
  let priceOfferType: PriceOfferType | undefined;
  if (isFamilyPriceAllowed && familySavings > guestSavings) {
    priceObjectWithSavings = familyPriceObject;
    priceOfferType = 'family';
  } else if (isBusinessPriceAllowed && businessSavings > guestSavings) {
    priceObjectWithSavings = businessPriceObject;
    priceOfferType = 'ibn';
  } else if (guestSavings > 0) {
    priceObjectWithSavings = guestPriceObject;
    priceOfferType = 'tro';
  }
  return {
    displayPrice: getPriceOfService(
      regularPrice,
      currencyCode,
      priceObjectWithSavings,
      priceOfferType,
    ),
    customerPrice: getCustomerPrice(
      customerType,
      allowedMemberDiscounts,
      isBusinessOperation(operation),
      regularPrice,
      currencyCode,
      familyPriceObject,
      businessPriceObject,
      guestPriceObject,
    ),
  };
};

export const toAvailableService =
  (
    operation: string,
    customerType: CustomerType,
    allowedMemberDiscounts: boolean,
  ) =>
  (service: AvailableServicesInZipResponse): AvailableService => {
    const {
      serviceProductId,
      serviceCode,
      locationType,
      prices,
      replacementService,
    } = service;
    const { displayPrice, customerPrice } = getServicePrice(
      prices,
      operation,
      customerType,
      allowedMemberDiscounts,
    );

    let toReplacementService: ReplacementService | undefined;
    if (replacementService) {
      const {
        displayPrice: replacementServiceDisplayPrice,
        customerPrice: replacementServiceCustomerPrice,
      } = getServicePrice(
        replacementService.prices || [],
        replacementService.operation,
        customerType,
        allowedMemberDiscounts,
      );
      toReplacementService = {
        serviceId: replacementService.serviceProductId,
        serviceCode: replacementService.serviceCode,
        operation: replacementService.operation,
        location: getLocation(replacementService.locationType),
        displayPrice: replacementServiceDisplayPrice,
        customerPrice: replacementServiceCustomerPrice,
      };
    }

    return {
      serviceId: serviceProductId,
      serviceCode,
      location: getLocation(locationType),
      displayPrice,
      customerPrice,
      replacementService: toReplacementService,
    };
  };

export const sortServicesByPrice = (
  serviceA: AvailableService,
  serviceB: AvailableService,
): number => {
  return serviceA.displayPrice.exclSavings - serviceB.displayPrice.exclSavings;
};

export const toAvailableStore = (
  store: AvailableStoresResponse,
): AvailableStore => {
  return {
    value: store.externalId,
    text: store.name,
    street: store.street,
    zipCode: store.zipCode,
    city: store.city,
  };
};

const toTimeSlot = (
  slot: AvailableTimeslotsResponse,
  isTimeInBrowserSettings: boolean,
): TimeSlots => {
  const { from, to, id, externalId } = slot;
  return {
    from: convertedTime(from, isTimeInBrowserSettings),
    to: convertedTime(to, isTimeInBrowserSettings),
    dateFrom: from,
    dateTo: to,
    id,
    appointmentId: externalId,
  };
};

const toSlot = (date: string, timeSlots: TimeSlots[]): Slots => {
  return {
    date,
    timeSlots,
  };
};

const toOneSlotPerTimeRange = (slots: Slots[]) => {
  return slots.map((slot) => {
    const timeSlotsGroupedByTimeRange = slot.timeSlots.reduce(
      (map, timeSlot) => {
        const timeRange = `${timeSlot.dateFrom}-${timeSlot.dateTo}`;
        const groupedSlots = map.get(timeRange) || [];
        groupedSlots.push(timeSlot);
        return map.set(timeRange, groupedSlots);
      },
      new Map<string, TimeSlots[]>(),
    );

    const filteredTimeSlots = Array.from(
      timeSlotsGroupedByTimeRange.values(),
    ).map((allSlots) => {
      return allSlots[Math.floor(Math.random() * allSlots.length)];
    });

    return { ...slot, timeSlots: filteredTimeSlots };
  });
};

export const toSlots = async (
  slotsPayload: AvailableTimeslotsResponse[],
  isTimeInBrowserSettings: boolean,
  firstDateToShow: string,
): Promise<Slots[]> => {
  const slots: Slots[] = slotsPayload.reduce(
    (acc: Slots[], slot: AvailableTimeslotsResponse) => {
      const dateWithoutTime = removeTimePartFromDate(slot.from);
      const isDateOutsideCalendar = isTimeInBrowserSettings
        ? isBefore(new Date(slot.from), new Date(firstDateToShow))
        : isBefore(parseISO(slot.from), parseISO(dateWithoutTime));

      if (isDateOutsideCalendar) {
        return acc;
      }

      const existingSlotIndex = acc.findIndex((prevSlot) => {
        if (isTimeInBrowserSettings) {
          return isSameDay(new Date(prevSlot.date), new Date(slot.from));
        }
        return isSameDay(parseISO(prevSlot.date), parseISO(dateWithoutTime));
      });

      if (existingSlotIndex === -1) {
        const timeSlot = toTimeSlot(slot, isTimeInBrowserSettings);
        const timeSlotDay = isTimeInBrowserSettings
          ? startOfDay(parseISO(slot.from)).toISOString()
          : dateWithoutTime;
        acc.push(toSlot(timeSlotDay, [timeSlot]));
        return acc;
      }

      acc[existingSlotIndex].timeSlots.push(
        toTimeSlot(slot, isTimeInBrowserSettings),
      );
      return acc;
    },
    [],
  );

  // This part ensures that our timeslots are sorted according to time (i.e. dateFrom-dateTo), regardless of how they are returned from BE
  await Promise.all(
    slots.map(
      (slot: Slots): Promise<Slots> =>
        new Promise((resolve): void => {
          const sortedSlot = sortTimeSlots(slot.timeSlots);
          resolve(sortedSlot as unknown as Slots);
        }),
    ),
  );

  const oneSlotPerTimeRange = toOneSlotPerTimeRange(slots);
  oneSlotPerTimeRange.sort((a: Slots, b: Slots): number => {
    const aDate = a.date;
    const bDate = b.date;
    if (aDate && bDate && aDate > bDate) {
      return 1;
    }
    if (aDate && bDate && aDate < bDate) {
      return -1;
    }
    return 0;
  });

  return oneSlotPerTimeRange;
};
